import { Button, Table, Menu, Dropdown, Modal, Spin } from 'antd';
import { TFunction, useTranslation } from 'react-i18next';
import { getCellValue } from 'utils/tableUtils';
import styles from './BuildsPage.module.less';
import { EllipsisOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
	deleteProjectBuild,
	getProjectBuilds,
	getProjectStatus,
	getLatestProjectBuild,
	releaseProjectOnStore,
} from 'redux/reducers/projectsSlice';
import { AppDispatch } from 'redux/store';
import { useDispatch } from 'react-redux';
import OmniNotification from 'components/OmniNotification';
import {
	ProjectStatusDto,
	ProductReleaseDto,
	ProductStatus,
	ReleaseChannel,
} from 'api';
import moment from 'moment';
import { History } from 'history';
import BuildModal from './Modal';
const getColumns = (
	t: TFunction,
	projectStatus: ProjectStatusDto,
	latestBuildId: string | undefined,
	history: History,
	callbacks: any,
) => {
	const columns = [
		{
			title: t('projectBuildsPage.Columns.ReleaseChannel'),
			key: 'rc',
			render: (record: any) => (
				<Button
					type="link"
					onClick={() => history.push('builds/' + record.releaseChannelId)}
				>
					{ReleaseChannel[getCellValue(record?.releaseChannelId)]}
				</Button>
			),
		},
		{
			title: t('projectBuildsPage.Columns.Name'),
			key: 'version',
			render: (record: any) => getCellValue(record?.buildName),
		},
		{
			title: t('projectBuildsPage.Columns.Version'),
			key: 'version',
			render: (record: any) => getCellValue(record?.version),
		},
		{
			title: t('projectBuildsPage.Columns.TestUsers'),
			key: 'testUsers',
			render: (record: any) => getCellValue(record?.users),
		},
		{
			title: t('projectBuildsPage.Columns.DateUpdated'),
			key: 'lastUpdated',
			render: (record: ProductReleaseDto) =>
				moment
					.parseZone(getCellValue(record.lastModifiedDate))
					.local()
					.format('MM-DD-YYYY h:mmA z'),
		},
	];

	const actionColumn = {
		title: t('projectBuildsPage.Columns.Actions'),
		key: 'actions',
		render: (record: any) => {
			return (
				<Dropdown
					overlay={getActionMenu(
						t,
						record,
						latestBuildId,
						projectStatus,
						callbacks,
					)}
					trigger={['click']}
				>
					<EllipsisOutlined onClick={(e) => e.preventDefault()} />
				</Dropdown>
			);
		},
	};

	return [...columns, actionColumn];
};

const getActionMenu = (
	t: TFunction,
	record: ProductReleaseDto,
	latestBuildId: string | undefined,
	projectStatus: ProjectStatusDto,
	callbacks: any,
) => {
	return (
		<Menu>
			{canReleaseOnStore(record, latestBuildId, projectStatus) && (
				<Menu.Item
					key="releaseOnStore"
					onClick={() => callbacks.releaseCallback(record)}
				>
					{t('projectBuildsPage.Actions.ReleaseOnStore')}
				</Menu.Item>
			)}
			<Menu.Item key="edit" onClick={() => callbacks.loadBuildCallback(record)}>
				{t('projectBuildsPage.Actions.Edit')}
			</Menu.Item>
			{record.releaseChannelId != ReleaseChannel.Store && (
				<Menu.Item
					key="delete"
					danger={true}
					onClick={() => callbacks.deleteBuildCallback(record)}
					data-testid="delete-action"
				>
					{t('projectBuildsPage.Actions.Delete')}
				</Menu.Item>
			)}
		</Menu>
	);
};

const canReleaseOnStore = (
	record: ProductReleaseDto,
	latestBuildId: string | undefined,
	projectStatus: ProjectStatusDto,
) => {
	return (
		!!projectStatus.status &&
		[ProductStatus.ApprovedForStore, ProductStatus.Released].includes(
			projectStatus.status,
		) &&
		record.id == latestBuildId &&
		record.releaseChannelId === ReleaseChannel.RC
	);
};

type ParamsProps = {
	projectId: string;
};
export default function BuildsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(true);
	const [buildData, setBuildData] = useState<ProductReleaseDto[]>();
	const [latestBuild, setLatestBuild] = useState<ProductReleaseDto>();
	const [buildModalVisible, setBuildModalVisible] = useState(false);
	const { projectId } = useParams<ParamsProps>();
	const [projectStatus, setProjectStatus] = useState<ProjectStatusDto>();
	const [buildUploading, setBuildUploading] = useState(false);
	const [selectedBuildInfo, setSelectedBuildInfo] =
		useState<ProductReleaseDto>();
	const dispatch: AppDispatch = useDispatch();
	const deleteBuildCallback = useCallback((record) => {
		Modal.confirm({
			icon: <InfoCircleOutlined />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('projectBuildsPage.Actions.Delete')}
				</span>
			),
			okText: t('common.confirm.delete'),
			cancelText: t('common.confirm.cancel'),
			onOk() {
				dispatch(
					deleteProjectBuild({
						projectId: parseInt(projectId),
						buildId: record.id,
					}),
				).then((result: any) => {
					if (!deleteProjectBuild.fulfilled.match(result)) {
						OmniNotification.error({
							message: t('projectBuildsPage.Actions.DeleteFail.Message'),
							description: result.error.message,
						});
						return;
					} else {
						OmniNotification.success({
							message: t('projectBuildsPage.Actions.Delete.Message'),
							description: t('projectBuildsPage.Actions.Delete.Description', {
								buildName: record.buildName,
							}),
						});
						fetchAllData();
					}
				});
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: <span>{t('projectBuildsPage.Actions.Delete.Modal')}</span>,
		});
	}, []);
	const releaseCallback = useCallback((record) => {
		dispatch(releaseProjectOnStore(parseInt(projectId))).then((result) => {
			OmniNotification.success({
				message: t('projectBuildsPage.Actions.ReleaseOnStore.Message'),
				description: t('projectBuildsPage.Actions.ReleaseOnStore.Description', {
					buildName: record.buildName,
				}),
			});
			fetchAllData();
		});
	}, []);
	const loadBuildCallback = useCallback((record) => {
		setSelectedBuildInfo(record);
		setBuildModalVisible(true);
	}, []);

	const hasBuilds = useMemo(() => !!buildData?.length, [buildData]);

	const columns = useMemo(
		() =>
			getColumns(
				t,
				projectStatus || ({} as ProjectStatusDto),
				latestBuild?.id,
				history,
				{
					deleteBuildCallback,
					loadBuildCallback,
					releaseCallback,
				},
			),
		[t, projectStatus, latestBuild?.id],
	);

	const fetchAllData = () => {
		setIsLoading(true);
		Promise.all([
			fetchProjectStatus(),
			getTableData(),
			fetchLatestBuild(),
		]).finally(() => {
			setIsLoading(false);
		});
	};

	useEffect(() => {
		fetchAllData();
	}, []);

	useEffect(() => {
		if (!buildModalVisible) {
			setSelectedBuildInfo(undefined);
		}
	}, [buildModalVisible]);

	const getTableData = () => {
		return dispatch(getProjectBuilds({ projectId: parseInt(projectId) })).then(
			(result) => {
				if (getProjectBuilds.fulfilled.match(result)) {
					setBuildData(result.payload.data);
				}
			},
		);
	};

	const fetchProjectStatus = async () => {
		return dispatch(getProjectStatus(parseInt(projectId))).then((result) => {
			if (getProjectStatus.fulfilled.match(result)) {
				setProjectStatus(result.payload);
			}
		});
	};

	const fetchLatestBuild = async () => {
		return dispatch(
			getLatestProjectBuild({ projectId: parseInt(projectId) }),
		).then((result) => {
			if (getLatestProjectBuild.fulfilled.match(result)) {
				setLatestBuild(result.payload);
			}
		});
	};

	return (
		<>
			<div className={styles.projectBuildsContainer}>
				<div className="page-head">
					<div>
						<h2 className="h2">{t('projectBuildsPage.Header')}</h2>
					</div>
					<div className={styles.buttonContainer}>
						<Button className="color-dark">
							<Link to={`publishing-requirements`}>
								{t('projectBuildsPage.PublishingRequirements')}
							</Link>
						</Button>
						<Button
							type="primary"
							onClick={() => setBuildModalVisible(true)}
							loading={buildUploading}
						>
							{t('projectBuildsPage.UploadBuild')}
						</Button>
					</div>
				</div>
				{isLoading && <Spin></Spin>}
				{!isLoading && (
					<Table
						className="table"
						size="middle"
						columns={columns}
						rowKey="id"
						dataSource={buildData}
						rowClassName={styles.tableRow}
						tableLayout="fixed"
						sortDirections={['descend', 'ascend', 'descend']}
						pagination={false}
					/>
				)}
			</div>
			<BuildModal
				setVisible={(visible: boolean) => setBuildModalVisible(visible)}
				afterSubmit={fetchAllData}
				projectId={parseInt(projectId)}
				projectStatus={projectStatus?.status}
				isUploading={setBuildUploading}
				visible={buildModalVisible}
				buildInfo={selectedBuildInfo}
				existingBuilds={buildData || []}
			/>
		</>
	);
}
